import React, { useState } from 'react';

import { SignInLinkButton } from './SignInLinkButton';

export function InviteForm({ onInvite, onGetLink }) {
  const [email, setEmail]       = useState('');
  const [name, setName]         = useState('');
  const [role, setRole]         = useState('apprentice');
  const [year, setYear]         = useState('1');
  const [studentNo, setStudentNo] = useState('');
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');
  const [success, setSuccess]   = useState('');
  const [manualLink, setManualLink] = useState('');
  const [invitedEmail, setInvitedEmail] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim() || !name.trim()) return setErr('Email and name are required.');
    setSaving(true);
    setErr('');
    setSuccess('');
    setManualLink('');
    try {
      const invitedName = name.trim();
      setInvitedEmail(email.trim().toLowerCase());
      const result = await onInvite(email.trim(), invitedName, role, Number(year), studentNo.trim() || null);
      // Only claim an email went out when one actually did.
      if (result?.emailSent === false) {
        setSuccess(`${invitedName} added as ${role}, but no invite email could be sent.`);
        setManualLink(result.link || '');
      } else {
        setSuccess(`Invite emailed to ${email.trim()} — they'll be asked to set a password.`);
      }
      setEmail(''); setName(''); setStudentNo('');
    } catch (e) {
      setErr(e.message || 'Failed to invite user');
    }
    setSaving(false);
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="frow">
        <div className="fgrp">
          <label className="flbl">Full Name *</label>
          <input className="finp" value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" />
        </div>
        <div className="fgrp">
          <label className="flbl">Email *</label>
          <input className="finp" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@example.com" />
        </div>
      </div>

      <div className="frow">
        <div className="fgrp">
          <label className="flbl">Role</label>
          <select className="fsel" value={role} onChange={e => setRole(e.target.value)}>
            <option value="apprentice">Apprentice</option>
            <option value="supervisor">Supervisor</option>
            <option value="trainer">Trainer</option>
            <option value="employer">Employer</option>
          </select>
        </div>
        {role === 'apprentice' && (
          <div className="fgrp">
            <label className="flbl">Year Level</label>
            <select className="fsel" value={year} onChange={e => setYear(e.target.value)}>
              <option value="1">Year 1</option>
              <option value="2">Year 2</option>
              <option value="3">Year 3</option>
            </select>
          </div>
        )}
      </div>

      {role === 'apprentice' && (
        <div className="fgrp">
          <label className="flbl">Student Number (optional)</label>
          <input className="finp" value={studentNo} onChange={e => setStudentNo(e.target.value)} placeholder="e.g. S12345678" />
        </div>
      )}

      {role === 'supervisor' && (
        <div style={{ padding: '6px 10px', borderRadius: 4, background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 11, color: '#1e40af', marginBottom: 12 }}>
          💡 Supervisors can review and approve evidence for their assigned apprentices. Assign apprentices after inviting.
        </div>
      )}

      {err && <div className="err">{err}</div>}
      {success && <div className="success-msg">{success}</div>}

      {/* Email was accepted, but delivery is never guaranteed — offer a link to
          send by hand without making them dig through Manage People for it. */}
      {success && !manualLink && invitedEmail && onGetLink && (
        <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 6, background: 'var(--panel)', border: '1px solid var(--border)', fontSize: 11.5 }}>
          <div style={{ marginBottom: 6, color: 'var(--muted)', lineHeight: 1.6 }}>
            Invite emails can be held up or filtered out. If it doesn't turn up, send
            them a sign-in link yourself:
          </div>
          <SignInLinkButton email={invitedEmail} onGetLink={onGetLink} label="Get sign-in link" />
        </div>
      )}

      {/* No email went out at all — hand over the link straight away */}
      {manualLink && (
        <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 6, background: 'rgba(245,166,35,.08)', border: '1px solid rgba(245,166,35,.3)', fontSize: 11.5 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Send them this sign-in link:</div>
          <div style={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 10.5, background: 'var(--bg)', padding: '6px 8px', borderRadius: 4, border: '1px solid var(--border)' }}>
            {manualLink}
          </div>
          <button
            type="button"
            className="btn-sm"
            style={{ fontSize: 10, marginTop: 6 }}
            onClick={() => navigator.clipboard?.writeText(manualLink)}
          >
            Copy link
          </button>
          <div style={{ marginTop: 6, color: 'var(--muted)' }}>
            The link expires — if it's gone stale they can use "Forgot password" instead.
          </div>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <button className="btn" type="submit" disabled={saving}>{saving ? 'Sending…' : 'Send Invite'}</button>
      </div>
    </form>
  );
}
